"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import { fetchWithAuth } from "@/lib/auth";
import { CalendarDays, CheckCheck, ChevronRight } from "lucide-react";
import type { NotificationType } from "./NotificationBell";
import { isEventNotification } from "./NotificationBell";
import { isStaffRole, volunteerPhaseSegment } from "@/lib/meetingPhaseRoute";
import { isSameEventDay } from "@/lib/eventTime";

function getEventTagBadge(tag?: string): { label: string; bg: string; text: string } {
    const t = (tag || "").toLowerCase();
    if (t === "pre_event") return { label: "Pre-Event", bg: "bg-teal-50 border-teal-200", text: "text-teal-700" };
    if (t === "mid_event") return { label: "Live / Mid", bg: "bg-emerald-50 border-emerald-200", text: "text-emerald-700" };
    if (t === "post_event" || t === "post_event_review") return { label: "Post-Event", bg: "bg-amber-50 border-amber-200", text: "text-amber-700" };
    if (t === "post_published" || t === "phase4_published") return { label: "Published", bg: "bg-blue-50 border-blue-200", text: "text-blue-700" };
    if (t === "chair_review") return { label: "Chair Review", bg: "bg-purple-50 border-purple-200", text: "text-purple-700" };
    if (t === "debrief" || t === "debrief_auto") return { label: "Debrief", bg: "bg-indigo-50 border-indigo-200", text: "text-indigo-700" };
    if (t === "nag") return { label: "Reminder", bg: "bg-rose-50 border-rose-200", text: "text-rose-700" };
    return { label: "Event", bg: "bg-slate-50 border-slate-200", text: "text-slate-700" };
}

function formatRelativeTime(dateStr: string): string {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function EventBell({
    onViewAll,
}: {
    /** Opens the full Notice Board / Upcoming Events panel. */
    onViewAll?: () => void;
} = {}) {
    const router = useRouter();
    const [notifications, setNotifications] = useState<NotificationType[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [filter, setFilter] = useState<"all" | "unread">("all");
    const [isMarkingAll, setIsMarkingAll] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const fetchEventNotifications = async () => {
        try {
            const notifRes = await fetchWithAuth(`/api/v1/notifications?category=event&limit=50`);
            if (notifRes.ok) {
                const notifs: NotificationType[] = await notifRes.json();
                const eventOnly = (notifs || []).filter(n => isEventNotification(n));
                eventOnly.sort(
                    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
                setNotifications(eventOnly);
            }
        } catch (err) {
            console.error("Failed to load event notifications:", err);
        }
    };

    const markRead = async (id: string) => {
        try {
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
            await fetchWithAuth(`/api/v1/notifications/${id}/read`, { method: "POST" });
        } catch (err) {
            console.error("Failed to mark event notification as read", err);
        }
    };

    const markAllRead = async () => {
        if (isMarkingAll) return;
        setIsMarkingAll(true);
        try {
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            await fetchWithAuth(`/api/v1/notifications/read-all?category=event`, { method: "POST" });
        } catch (err) {
            console.error("Failed to mark all event notifications as read", err);
        } finally {
            setIsMarkingAll(false);
        }
    };

    const handleNotificationClick = async (notif: NotificationType) => {
        if (!notif.is_read) {
            markRead(notif.id);
        }

        if (notif.entity_type === "meeting" && notif.entity_id) {
            try {
                // Fetch profile to determine dashboard prefix
                const profile = await fetchWithAuth("/api/v1/auth/me").then(r => r.json());
                const assignments = profile.assignments || [];
                const role = (profile.role || "volunteer").toLowerCase();

                // Staff is the system role, full stop. A Staff/Board Liaison assignment
                // used to count here too, which routed a group liaison into the staff
                // review queue and monitor — they are capped at chair level and belong
                // on the member-facing pages. See app/core/roles.py on the backend.
                const isStaff = isStaffRole(role);
                
                const isChair = assignments.some((a: any) => {
                    const r = (a.role || "").toLowerCase();
                    return r.includes("chair") || r.includes("lead");
                });

                // Fetch meeting details for entity alignment
                const meetingRes = await fetchWithAuth(`/api/v1/meetings/${notif.entity_id}`);
                const meeting = await meetingRes.json();
                const groupId = meeting.entity_id;
                const meetingId = notif.entity_id;

                let targetPath = "";

                // A temporary event helper always lands on the member-facing event
                // workspace, whatever their system role or the event's status: that
                // is the only shell whose gates know about helper grants, and the
                // staff/chair branches below would send them somewhere they'd 403.
                if (notif.tag === "event_helper") {
                    targetPath = `/groups/${meeting.entity_type}/${groupId}/meetings/${meetingId}/contribute`;
                } else if (isStaff && meeting.status === "pending_staff_review") {
                    targetPath = `/dashboard/manage/reviews/${meetingId}`;
                } else if (!isStaff && !isChair && ["approved", "live", "post_event", "completed"].includes(meeting.status)) {
                    targetPath = `/groups/${meeting.entity_type}/${groupId}/meetings/${meetingId}/contribute`;
                } else {
                    const basePrefix = `/dashboard/manage/groups/${meeting.entity_type}/${groupId}/meetings/${meetingId}`;
                    targetPath = basePrefix;

                    if (isStaff && ["approved", "live", "completed"].includes(meeting.status)) {
                        targetPath += "/monitor";
                    } else if (notif.tag === "pre_event" || notif.tag === "nag") {
                        targetPath += "/reporting/pre";
                    } else if (notif.tag === "mid_event") {
                        targetPath += "/reporting/live";
                    } else if (notif.tag === "post_event") {
                        targetPath += "/reporting/review";
                    } else if (notif.tag === "chair_review") {
                        targetPath += "/pre-review";
                    } else {
                        if (meeting.status === "approved") {
                            const isPast = new Date(meeting.start_at) < new Date();
                            // "Today" means today where the EVENT is. Comparing
                            // toDateString() projected both dates into the viewer's
                            // calendar, so anyone offset from the event's zone was
                            // routed to /reporting/pre or /review on the day itself
                            // and could not reach the live workspace from the bell.
                            const isToday = isSameEventDay(
                                meeting.start_at,
                                new Date().toISOString(),
                                meeting.meeting_timezone,
                            );
                            if (isToday) targetPath += "/reporting/live";
                            else if (isPast) targetPath += "/reporting/review";
                            else targetPath += "/reporting/pre";
                        } else if (isStaff) {
                            targetPath += "/pre-review";
                        } else {
                            // A member has nothing to do on an event sitting with staff —
                            // /pre-review would show them the Phase-02 review surface.
                            targetPath += `/${volunteerPhaseSegment(meeting)}`;
                        }
                    }
                }

                setIsOpen(false);
                router.push(targetPath);
            } catch (e) {
                console.error("Failed to route event notification:", e);
                setIsOpen(false);
            }
        } else {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        fetchEventNotifications();
        const intervalId = setInterval(fetchEventNotifications, 60000);
        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const unreadCount = notifications.filter(n => !n.is_read).length;
    const displayedNotifs = filter === "unread" ? notifications.filter(n => !n.is_read) : notifications;

    return (
        <div className="relative" ref={dropdownRef}>
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className={`relative p-2 rounded-xl transition-all ${
                    isOpen 
                        ? "bg-[#007a8a]/10 text-[#007a8a]" 
                        : "hover:bg-black/5 dark:hover:bg-white/10 text-slate-700"
                }`}
                aria-label="Event Notices"
                title="Event Notices"
            >
                <CalendarDays className="w-5 h-5" strokeWidth={2.2} />
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[16px] h-[16px] rounded-full bg-[#ae232d] text-white text-[9px] font-black flex items-center justify-center px-0.5 shadow-sm ring-2 ring-white">
                        {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-84 sm:w-96 max-h-[30rem] overflow-hidden flex flex-col bg-white border border-slate-200 shadow-2xl rounded-2xl z-50 text-slate-800 font-sans animate-in fade-in zoom-in-95 duration-150">
                    {/* Header */}
                    <div className="p-3.5 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <CalendarDays className="w-4 h-4 text-[#007a8a]" />
                            <span className="font-bold text-sm text-[#003d45]">Event Notices</span>
                            {unreadCount > 0 && (
                                <span className="text-[10px] font-black bg-[#ae232d] text-white px-1.5 py-0.5 rounded-full">
                                    {unreadCount > 99 ? "99+" : unreadCount} New
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllRead}
                                    disabled={isMarkingAll}
                                    className="text-[11px] font-semibold text-[#007a8a] hover:text-[#003d45] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-200/60 transition-colors disabled:opacity-50"
                                    title="Mark all events read"
                                >
                                    <CheckCheck className="w-3.5 h-3.5" />
                                    <span>Mark all read</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex items-center px-3.5 py-2 border-b border-slate-100 gap-2 bg-white text-xs">
                        <button
                            onClick={() => setFilter("all")}
                            className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-colors ${
                                filter === "all"
                                    ? "bg-[#007a8a] text-white"
                                    : "text-slate-500 hover:bg-slate-100"
                            }`}
                        >
                            All ({notifications.length})
                        </button>
                        <button
                            onClick={() => setFilter("unread")}
                            className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-colors ${
                                filter === "unread"
                                    ? "bg-[#ae232d] text-white"
                                    : "text-slate-500 hover:bg-slate-100"
                            }`}
                        >
                            Unread ({unreadCount})
                        </button>
                    </div>

                    {/* Notification List */}
                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100 max-h-80">
                        {displayedNotifs.length === 0 ? (
                            <div className="p-8 text-center">
                                <CalendarDays className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                <p className="text-xs font-semibold text-slate-600">
                                    {filter === "unread" ? "No unread event notices" : "No event notices yet"}
                                </p>
                                <p className="text-[11px] text-slate-400 mt-0.5">
                                    Pre-event guidelines and reporting updates will appear here.
                                </p>
                            </div>
                        ) : (
                            displayedNotifs.map((notif) => {
                                const badge = getEventTagBadge(notif.tag);
                                return (
                                    <div 
                                        key={notif.id} 
                                        className={`p-3.5 text-sm transition-all cursor-pointer group ${
                                            notif.is_read 
                                                ? "bg-white hover:bg-slate-50/80 opacity-80 hover:opacity-100" 
                                                : "bg-[#f4fbfb]/70 hover:bg-[#eaf6f6]"
                                        }`}
                                        onClick={() => handleNotificationClick(notif)}
                                    >
                                        <div className="flex items-center justify-between gap-2 mb-1.5">
                                            <span className={`text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md border ${badge.bg} ${badge.text}`}>
                                                {badge.label}
                                            </span>
                                            <span className="text-[10px] font-medium text-slate-400">
                                                {formatRelativeTime(notif.created_at)}
                                            </span>
                                        </div>
                                        <div className="flex items-start justify-between gap-2">
                                            <b className={`text-xs block leading-snug ${notif.is_read ? 'text-slate-700 font-semibold' : 'text-slate-900 font-bold'}`}>
                                                {notif.title}
                                            </b>
                                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#007a8a] group-hover:translate-x-0.5 transition-all flex-shrink-0 mt-0.5" />
                                        </div>
                                        <p className={`text-[11px] mt-1 line-clamp-2 leading-relaxed ${notif.is_read ? 'text-slate-500' : 'text-slate-600'}`}>
                                            {notif.message}
                                        </p>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {onViewAll && (
                        <div className="border-t border-slate-100 bg-slate-50/50 p-2 text-center">
                            <button
                                onClick={() => { setIsOpen(false); onViewAll(); }}
                                className="w-full py-1.5 text-center text-xs font-bold text-[#007a8a] hover:text-[#003d45] hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                View full events & notice board
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
